import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import ProductCard from '../components/common/ProductCard';
import ViewingCounter from '../components/common/ViewingCounter';
import ProductImage from '../components/ProductImage';
import Hero3D from '../components/Hero3D';


const Home = () => {
  const [categories, setCategories] = useState([]);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [categoriesRes, productsRes] = await Promise.all([
        api.get('/categories'),
        api.get('/products/featured?limit=8'),
      ]);
      setCategories(categoriesRes.data?.categories || []);
      setFeaturedProducts(productsRes.data?.products || []);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load products. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-bg">
      {/* 3D Hero Section - Always Visible */}
      <Hero3D />

      <div className="container mx-auto px-6 mt-12 flex justify-center">
        <ViewingCounter min={5} max={50} label="fashionistas exploring right now" />
      </div>

      {/* Categories Showcase */}
      <section className="container mx-auto px-6 py-12 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-5xl font-display font-bold mb-4 uppercase tracking-tight">Shop by Category</h2>
          <div className="h-1 w-16 md:w-20 bg-primary-light mx-auto" />
        </motion.div>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="skeleton h-48 md:h-64 rounded-xl" />
            ))}
          </div>
        ) : error ? (
          <div className="text-center p-8 bg-red-900/10 rounded-xl border border-red-500/20 max-w-lg mx-auto">
            <p className="text-red-400 mb-4">Unable to load categories</p>
            <button onClick={() => { setLoading(true); loadData(); }} className="btn-secondary text-sm">Retry</button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.map((category, i) => (
              <motion.div
                key={category._id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Link to={`/products?category=${category.slug}`}>
                  <div className="relative h-60 md:h-80 rounded-xl overflow-hidden group cursor-pointer border border-gray-800 hover:border-primary-light/50 transition-all duration-500">
                    <ProductImage
                      src={category.image}
                      alt={category.name}
                      className="w-full h-full transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent flex items-end p-4 md:p-6">
                      <h3 className="text-white font-bold text-lg md:text-2xl group-hover:text-primary-light transition-colors">{category.name}</h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Featured Products */}
      <section className="bg-dark-card py-12 md:py-24 border-t border-white/5">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center md:items-end justify-between mb-12 md:mb-16 gap-6">
            <div className="text-center md:text-left">
              <h2 className="text-3xl md:text-5xl font-display font-bold mb-4 uppercase tracking-tight">Featured Gear</h2>
              <div className="h-1 w-16 md:w-20 bg-primary-light mx-auto md:mx-0" />
            </div>
            <Link to="/products" className="btn-secondary w-full md:w-auto text-center px-8">
              View All Products
            </Link>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-96 rounded-xl" />
              ))}
            </div>
          ) : error ? (
            <div className="text-center p-12 bg-red-900/10 rounded-xl border border-red-500/20">
              <p className="text-red-400 mb-4">Failed to load featured products</p>
              <button onClick={() => { setLoading(true); loadData(); }} className="btn-primary">Retry Loading</button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {featuredProducts.map((product) => (
                <ProductCard key={product._id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Home;
